import React from 'react'
import { observer } from 'mobx-react-lite'

import Checkbox from '@mui/material/Checkbox'
import IconButton from '@mui/material/IconButton'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'

import { P, Text, TextField } from '@mxenabled/mxui'
import { Delete as DeleteOutlinedIcon, Edit as EditIcon } from '@mxenabled/mx-icons'

import { Dialog, useGlobalCopyStore } from '../../../common'

import type { Tag } from '../../../core'

import { useEvent, useTransactionStore } from '../../../common'
import { ON_CLICK_EVENTS } from '../../constants/OnClickEvents'

interface TagRowProps {
  tag: Tag
  selectedTags: string[]
  onTagChecked: (guid: string) => void
}

const TagRow: React.FC<TagRowProps> = ({ onTagChecked, selectedTags, tag }) => {
  const { onEvent } = useEvent()
  const { removeTag, updateTag } = useTransactionStore()
  const { common: commonCopy, transactions: copy } = useGlobalCopyStore()

  const [editOpen, setEditOpen] = React.useState(false)
  const [deleteOpen, setDeleteOpen] = React.useState(false)

  const [tagName, setTagName] = React.useState('')
  const [tagNameError, setTagNameError] = React.useState('')

  const handleSaveTag = async () => {
    onEvent(ON_CLICK_EVENTS.ON_TRANSACTION_TAGS_TAG_EDIT_SAVED)
    if (tag) {
      await updateTag({ ...tag, name: tagName })
    }
    setEditOpen(false)
  }

  const handleDeleteTag = async () => {
    onEvent(ON_CLICK_EVENTS.ON_TRANSACTION_TAGS_TAG_DELETED)
    await removeTag(tag.guid)
    setDeleteOpen(false)
  }

  React.useEffect(() => {
    setTagNameError(tagName.length > 40 ? copy.tags_error_name_must_be_40_chars : '')
  }, [tagName])

  const handleEditClick = () => {
    onEvent(ON_CLICK_EVENTS.ON_TRANSACTION_TAGS_TAG_EDIT_CLICK)
    setTagName(tag.name)
    setEditOpen(true)
  }

  const handleDeleteClick = () => {
    onEvent(ON_CLICK_EVENTS.ON_TRANSACTION_TAGS_TAG_DELETE_CLICK)
    setDeleteOpen(true)
  }

  return (
    <ListItem
      className="mx-txn-transaction-manager-row"
      disableGutters={true}
      disablePadding={true}
      secondaryAction={
        !tag.is_default_tag && (
          <Stack direction="row">
            <IconButton
              aria-label={`Edit ${tag.name} tag`}
              color="secondary"
              onClick={handleEditClick}
            >
              <EditIcon color="secondary" />
            </IconButton>
            <IconButton
              aria-label={`Delete ${tag.name} tag`}
              color="secondary"
              onClick={handleDeleteClick}
            >
              <DeleteOutlinedIcon color="secondary" />
            </IconButton>
          </Stack>
        )
      }
    >
      <ListItemButton
        aria-checked={selectedTags.includes(tag.guid)}
        onClick={() => onTagChecked(tag.guid)}
        role="checkbox"
        tabIndex={0}
      >
        <ListItemIcon sx={{ ml: 0 }}>
          <Checkbox
            aria-hidden={true}
            checked={selectedTags.includes(tag.guid)}
            edge="start"
            tabIndex={-1}
          />
        </ListItemIcon>
        <ListItemText>{tag.name}</ListItemText>
      </ListItemButton>

      <Dialog
        copy={{ title: 'Update tag name' }} // TODO: Need copy
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        onPrimaryAction={handleSaveTag}
        onSecondaryAction={() => setEditOpen(false)}
      >
        <TextField
          aria-describedby={tagNameError ? 'update-tag-error' : undefined}
          aria-label={copy.tags_update_the_tag_name}
          name="update-tag"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTagName(e.target.value)}
          placeholder="Tag name" // TODO: Need copy
          sx={{ width: '100%' }}
          value={tagName}
        />
        <P id="update-tag-error" sx={{ color: 'error.main' }} variant="subtitle2">
          {tagNameError}
        </P>
      </Dialog>

      <Dialog
        copy={{ title: copy.tags_delete_title }}
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onPrimaryAction={handleDeleteTag}
        onSecondaryAction={() =>
          onEvent(ON_CLICK_EVENTS.ON_TRANSACTION_TAGS_TAG_DELETE_CANCEL_CLICK)
        }
        primaryText={commonCopy.delete_button}
        secondaryText={commonCopy.cancel_button}
      >
        <Text truncate={false}>{copy.tags_deleting_this_tag_will_remove_it}</Text>
      </Dialog>
    </ListItem>
  )
}

export default observer(TagRow)
